import type {
  Adsr,
  FilterConfig,
  Instrument,
  SamplerLfoWave,
  SamplerModRoute,
  SamplerPreset,
  SamplerZone,
  SampleSlice,
} from "../types";
import { deepClone } from "../kernel/deepClone";
import { FACTORY_CYCLE_IDS, defaultFactorySampleId } from "./factoryCycles";

/** Chromatic pad grid size (Author Chop / map). */
export const SAMPLER_PAD_COUNT = 32;
/** MIDI note for pad 1 (C4). */
export const SAMPLER_PAD_BASE_NOTE = 60;

function clamp(value: number, min: number, max: number): number {
  return Math.min(max, Math.max(min, value));
}

const DEFAULT_FILTER: FilterConfig = {
  type: "lowpass",
  cutoffHz: 12_000,
  resonance: 0.15,
};

const DEFAULT_AMP: Adsr = { a: 0.005, d: 0.15, s: 0.85, r: 0.25 };
const DEFAULT_MOD: Adsr = { a: 0.01, d: 0.25, s: 0.4, r: 0.3 };

const LFO_WAVES: readonly SamplerLfoWave[] = [
  "sine",
  "tri",
  "square",
  "saw",
  "snh",
  "random",
];

function normalizeLfoWave(raw: unknown): SamplerLfoWave {
  return typeof raw === "string" && (LFO_WAVES as readonly string[]).includes(raw)
    ? (raw as SamplerLfoWave)
    : "sine";
}

function normalizeAdsr(raw: Partial<Adsr> | undefined, fallback: Adsr): Adsr {
  return {
    a: clamp(Number(raw?.a ?? fallback.a), 0, 8),
    d: clamp(Number(raw?.d ?? fallback.d), 0, 8),
    s: clamp(Number(raw?.s ?? fallback.s), 0, 1),
    r: clamp(Number(raw?.r ?? fallback.r), 0, 8),
  };
}

function normalizeFilter(raw: Partial<FilterConfig> | undefined): FilterConfig {
  const type = raw?.type;
  return {
    type: type === "highpass" || type === "bandpass" || type === "lowpass" ? type : "lowpass",
    cutoffHz: clamp(Number(raw?.cutoffHz ?? DEFAULT_FILTER.cutoffHz), 40, 20_000),
    resonance: clamp(Number(raw?.resonance ?? DEFAULT_FILTER.resonance), 0, 1),
  };
}

function normalizeRoutes(raw: unknown): SamplerModRoute[] {
  if (!Array.isArray(raw)) {
    return [];
  }
  const out: SamplerModRoute[] = [];
  for (const item of raw) {
    if (!item || typeof item !== "object") {
      continue;
    }
    const route = item as Partial<SamplerModRoute>;
    if (typeof route.source !== "string" || typeof route.target !== "string") {
      continue;
    }
    out.push({
      source: route.source as SamplerModRoute["source"],
      target: route.target as SamplerModRoute["target"],
      depth: Number(route.depth) || 0,
      ...(route.invert ? { invert: true } : {}),
    });
  }
  return out;
}

function normalizeZone(raw: Partial<SamplerZone> | undefined, fallbackSampleId: string): SamplerZone {
  const id = typeof raw?.id === "string" && raw.id ? raw.id : cryptoRandomId("zone");
  const sampleId =
    typeof raw?.sampleId === "string" && raw.sampleId ? raw.sampleId : fallbackSampleId;
  const keyLow = clamp(Math.round(Number(raw?.keyLow ?? 0)), 0, 127);
  const keyHigh = clamp(Math.round(Number(raw?.keyHigh ?? 127)), keyLow, 127);
  const velLow = clamp(Math.round(Number(raw?.velLow ?? 0)), 0, 127);
  const velHigh = clamp(Math.round(Number(raw?.velHigh ?? 127)), velLow, 127);
  const zone: SamplerZone = {
    id,
    sampleId,
    keyLow,
    keyHigh,
    rootKey: clamp(Math.round(Number(raw?.rootKey ?? 60)), 0, 127),
    velLow,
    velHigh,
  };
  if (raw?.region && typeof raw.region === "object") {
    const start = Math.max(0, Math.round(Number(raw.region.start ?? 0)));
    const end = Math.max(start + 1, Math.round(Number(raw.region.end ?? start + 1)));
    zone.region = { start, end };
  }
  if (raw?.tuneCents != null) {
    zone.tuneCents = clamp(Number(raw.tuneCents), -1200, 1200);
  }
  if (raw?.gainDb != null) {
    zone.gainDb = clamp(Number(raw.gainDb), -48, 24);
  }
  if (raw?.reverse) {
    zone.reverse = true;
  }
  if (raw?.padIndex != null) {
    zone.padIndex = clamp(Math.round(Number(raw.padIndex)), 0, SAMPLER_PAD_COUNT - 1);
  }
  return zone;
}

function normalizeSlices(raw: unknown): SampleSlice[] | undefined {
  if (!Array.isArray(raw) || raw.length === 0) {
    return undefined;
  }
  const out: SampleSlice[] = [];
  for (const item of raw) {
    if (!item || typeof item !== "object") {
      continue;
    }
    const slice = item as Partial<SampleSlice>;
    if (typeof slice.id !== "string" || !slice.id) {
      continue;
    }
    out.push({
      id: slice.id,
      frame: Math.max(0, Math.round(Number(slice.frame ?? 0))),
      ...(typeof slice.label === "string" ? { label: slice.label } : {}),
      ...(slice.color != null ? { color: Number(slice.color) } : {}),
      ...(slice.omit === true ? { omit: true } : {}),
    });
  }
  return out.length > 0 ? out : undefined;
}

function cryptoRandomId(prefix: string): string {
  if (typeof crypto !== "undefined" && "randomUUID" in crypto) {
    return `${prefix}_${crypto.randomUUID().slice(0, 8)}`;
  }
  return `${prefix}_${Math.random().toString(36).slice(2, 10)}`;
}

export function createDefaultSamplerPreset(name = "Sampler"): SamplerPreset {
  const sampleId = defaultFactorySampleId();
  return {
    name,
    playMode: "classic",
    preserveDuration: false,
    region: { start: 0, end: 0 },
    loop: { enabled: true, start: 0, end: 0, mode: "continuous" },
    reverse: false,
    granular: {
      grainSizeMs: 40,
      density: 8,
      spray: 0.1,
      window: "hann",
      stretch: 1,
    },
    filter: { ...DEFAULT_FILTER },
    ampEnv: { ...DEFAULT_AMP },
    modEnv: { ...DEFAULT_MOD },
    lfo: { rateHz: 4, wave: "sine", depth: 0 },
    lfo2: { rateHz: 2, wave: "square", depth: 0 },
    routes: [],
    maxVoices: 16,
    primarySampleId: sampleId,
    zones: [
      {
        id: "zone_main",
        sampleId,
        keyLow: 0,
        keyHigh: 127,
        rootKey: 60,
        velLow: 0,
        velHigh: 127,
      },
    ],
  };
}

export function normalizeSamplerPreset(raw: unknown): SamplerPreset {
  const fallback = createDefaultSamplerPreset();
  if (!raw || typeof raw !== "object") {
    return fallback;
  }
  const obj = raw as Partial<SamplerPreset>;
  const primarySampleId =
    typeof obj.primarySampleId === "string" && obj.primarySampleId
      ? obj.primarySampleId
      : fallback.primarySampleId!;

  const zonesIn = Array.isArray(obj.zones) ? obj.zones : [];
  const zones =
    zonesIn.length > 0
      ? zonesIn.map(z => normalizeZone(z as Partial<SamplerZone>, primarySampleId))
      : [normalizeZone({ ...fallback.zones[0], sampleId: primarySampleId }, primarySampleId)];

  const regionStart = Math.max(0, Math.round(Number(obj.region?.start ?? 0)));
  const regionEnd = Math.max(regionStart, Math.round(Number(obj.region?.end ?? 0)));

  const loopStart = Math.max(0, Math.round(Number(obj.loop?.start ?? 0)));
  const loopEnd = Math.max(loopStart, Math.round(Number(obj.loop?.end ?? 0)));

  const granularRaw = obj.granular ?? fallback.granular;
  const window =
    granularRaw.window === "tri" || granularRaw.window === "rect" || granularRaw.window === "hann"
      ? granularRaw.window
      : "hann";

  const loopEnabledDefault = FACTORY_CYCLE_IDS.has(primarySampleId);
  const loopEnabled =
    obj.loop && "enabled" in obj.loop && obj.loop.enabled != null
      ? obj.loop.enabled === true
      : loopEnabledDefault;
  const loopModeDefault = loopEnabledDefault ? "continuous" : "untilRelease";
  const loopMode =
    obj.loop?.mode === "continuous" || obj.loop?.mode === "untilRelease"
      ? obj.loop.mode
      : loopModeDefault;

  const lfo2Raw = obj.lfo2;
  const preset: SamplerPreset = {
    name:
      typeof obj.name === "string" && obj.name.trim()
        ? obj.name.trim().slice(0, 64)
        : fallback.name,
    playMode: obj.playMode === "granular" ? "granular" : "classic",
    preserveDuration: obj.preserveDuration === true,
    region: { start: regionStart, end: regionEnd },
    loop: {
      enabled: loopEnabled,
      start: loopStart,
      end: loopEnd,
      mode: loopMode,
      ...(obj.loop?.xfade != null
        ? { xfade: clamp(Math.round(Number(obj.loop.xfade)), 0, 4096) }
        : {}),
    },
    reverse: obj.reverse === true,
    granular: {
      grainSizeMs: clamp(Number(granularRaw.grainSizeMs ?? 40), 1, 500),
      density: clamp(Number(granularRaw.density ?? 8), 0.1, 64),
      spray: clamp(Number(granularRaw.spray ?? 0.1), 0, 1),
      window,
      stretch: clamp(Number(granularRaw.stretch ?? 1), 0.01, 8),
    },
    filter: normalizeFilter(obj.filter),
    ampEnv: normalizeAdsr(obj.ampEnv, DEFAULT_AMP),
    modEnv: normalizeAdsr(obj.modEnv, DEFAULT_MOD),
    lfo: {
      rateHz: clamp(Number(obj.lfo?.rateHz ?? 4), 0.01, 40),
      wave: normalizeLfoWave(obj.lfo?.wave),
      depth: clamp(Number(obj.lfo?.depth ?? 0), 0, 1),
    },
    lfo2: {
      rateHz: clamp(Number(lfo2Raw?.rateHz ?? 2), 0.01, 40),
      wave: normalizeLfoWave(lfo2Raw?.wave ?? "square"),
      depth: clamp(Number(lfo2Raw?.depth ?? 0), 0, 1),
    },
    routes: normalizeRoutes(obj.routes),
    maxVoices: clamp(Math.round(Number(obj.maxVoices ?? 16)), 1, 32),
    zones,
    primarySampleId,
  };

  if (obj.glideMs != null) {
    preset.glideMs = clamp(Number(obj.glideMs), 0, 2000);
  }
  const slices = normalizeSlices(obj.slices);
  if (slices) {
    preset.slices = slices;
  }

  return preset;
}

export function cloneSamplerPreset(preset: SamplerPreset): SamplerPreset {
  return deepClone(normalizeSamplerPreset(preset));
}

export function samplerInstrument(preset: SamplerPreset): Instrument {
  return { kind: "sampler", preset: cloneSamplerPreset(preset) };
}

export function isSamplerInstrument(
  instrument: Instrument,
): instrument is { kind: "sampler"; preset: SamplerPreset } {
  return instrument.kind === "sampler";
}

/** Collect unique sample ids referenced by a preset. */
export function samplerPresetSampleIds(preset: SamplerPreset): string[] {
  const ids = new Set<string>();
  if (preset.primarySampleId) {
    ids.add(preset.primarySampleId);
  }
  for (const zone of preset.zones) {
    if (zone.sampleId) {
      ids.add(zone.sampleId);
    }
  }
  return [...ids];
}

/** Pick the zone for a MIDI note + velocity, or null. */
export function findSamplerZone(
  preset: SamplerPreset,
  note: number,
  velocityMidi: number,
): SamplerZone | null {
  const n = Math.round(note);
  const v = Math.round(velocityMidi);
  for (const zone of preset.zones) {
    if (n < zone.keyLow || n > zone.keyHigh) {
      continue;
    }
    if (v < zone.velLow || v > zone.velHigh) {
      continue;
    }
    return zone;
  }
  return null;
}
